import { add, isAfter, isEqual } from 'date-fns';

import type { StepsConfig } from 'cadenza/conditions/date-time-condition/steps-config';
import { TickUnit } from 'cadenza/conditions/model/tick-unit';
import { differenceInTickUnit } from 'cadenza/conditions/date-time-condition/date-time-condition-utils';
import i18n from 'cadenza/conditions/actions/animation-settings-dialog.properties';

export interface AutomaticFrameDistributionResult {
  stepsConfig: StepsConfig;
  message?: string;
}

interface Step {
  unit: TickUnit;
  timeWindow: number;
}

/**
 * Tries to calculate a animation frame distribution based on predefined steps that covers the whole
 * date range.
 *
 * @param maxFrames - The maximum allowed number of frames.
 * @param rangeStart - The start of the date range.
 * @param rangeEnd - The end of the date range.
 * @param smallestUnit - The smallest allowed date unit.
 * @return - A steps config that covers the whole date range or if the date range cannot be covered, the largest step size and a warning message.
 */
export function calculateAutomaticFrameDistribution (maxFrames: number, rangeStart: Date, rangeEnd: Date, smallestUnit: TickUnit): AutomaticFrameDistributionResult {
  let doesNotCoverAllData;
  const stepConfig = predefinedSteps.find((step, index) => {
    const numFramesForStepUnit = differenceInTickUnit(rangeStart, rangeEnd, step.unit);
    const numFramesForTimeWindow = Math.ceil(numFramesForStepUnit / step.timeWindow);
    if (numFramesForTimeWindow > maxFrames || TickUnit.order(step.unit) < TickUnit.order(smallestUnit)) {
      if (index === predefinedSteps.length - 1) { // is maximum time window size reached?
        doesNotCoverAllData = true;
        return true;
      }
      return false;
    }
    if (numFramesForTimeWindow <= maxFrames) { // do number of frames fit?
      const calculatedEndDate = add(rangeStart, { [ step.unit ]: maxFrames * step.timeWindow });
      if (isEqual(rangeEnd, calculatedEndDate) || isAfter(calculatedEndDate, rangeEnd)) { // is the whole date range covered?
        return true;
      }
    }
    return false;
  });

  const result: AutomaticFrameDistributionResult = {
    stepsConfig: {
      unit: stepConfig!.unit,
      timeWindow: stepConfig!.timeWindow,
      overlap: 0
    }
  };
  if (doesNotCoverAllData) {
    result.message = i18n('animation.settings.automatic-distribution.out-of-range');
  }
  return result;
}

const secondsAndMinutesDurations = [ 1, 5, 10, 15, 30 ];
const hoursDurations = [ 1, 2, 6, 12 ];
const daysAndWeeksDurations = [ 1, 2 ];
const monthsDurations = [ 1, 2, 3, 6 ];
const yearsDurations = [ 1, 2, 5, 10, 20, 50, 100 ];
const predefinedSteps: Step[] =
  secondsAndMinutesDurations.map((duration) => toStep('seconds', duration)).concat(
    secondsAndMinutesDurations.map((duration) => toStep('minutes', duration)),
    hoursDurations.map((duration) => toStep('hours', duration)),
    daysAndWeeksDurations.map((duration) => toStep('days', duration)),
    daysAndWeeksDurations.map((duration) => toStep('weeks', duration)),
    monthsDurations.map((duration) => toStep('months', duration)),
    yearsDurations.map((duration) => toStep('years', duration))
  );

function toStep (unit: TickUnit, timeWindow: number): Step {
  return {
    unit,
    timeWindow
  };
}
